import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../contexts/WalletContext';
import { apiService } from '../services/api';

const CreateDonationPage: React.FC = () => {
  const { connected, publicKey } = useWallet();
  const navigate = useNavigate();
  const [minAmount, setMinAmount] = useState<number>(0.001);
  const [description, setDescription] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!connected || !publicKey) {
      setError('Please connect your wallet first');
      return;
    }

    if (minAmount < 0.001) {
      setError('Minimum amount must be at least 0.001 SOL');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiService.createDonationPage({
        public_key: publicKey.toString(),
        min_amount: minAmount,
        description: description,
      });

      if (response.success) {
        // Redirect to dashboard after successful creation
        navigate('/dashboard');
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to create donation page. Please try again.');
      console.error('Error creating donation page:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  if (!connected) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="text-6xl mb-6">🔗</div>
          <h2 className="text-3xl font-bold mb-4">Connect Your Wallet</h2>
          <p className="text-xl text-gray-300 mb-8">
            Please connect your Phantom wallet to create a donation page
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ paddingTop: '48px', paddingBottom: '32px' }}>
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="btn-ghost mb-6"
          >
            ← Back to Dashboard
          </button>
          <h1 className="text-4xl font-bold mb-4">
            Create New <span className="gradient-text">Donation Page</span>
          </h1>
          <p className="text-xl text-gray-300">
            Set up your donation page to start receiving SOL from your viewers
          </p>
        </div>

        {/* Form */}
        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="minAmount" className="block text-lg font-semibold text-white" style={{ marginBottom: '16px !important' }}>
                Minimum Donation Amount (SOL)
              </label>
              <input
                type="number"
                id="minAmount"
                step="0.001"
                min="0.001"
                value={minAmount}
                onChange={(e) => setMinAmount(parseFloat(e.target.value))}
                className="input-field text-lg"
                placeholder="0.001"
                required
                style={{ marginTop: '16px' }}
              />
              <p className="text-sm text-gray-400 mt-2">
                Minimum amount viewers can donate (recommended: 0.001 SOL)
              </p>
            </div>

            <div>
              <label htmlFor="description" className="block text-lg font-semibold text-white" style={{ marginBottom: '16px !important' }}>
                Page Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="input-field text-lg resize-none"
                placeholder="Tell your supporters what you're raising funds for... (optional)"
                style={{ marginTop: '16px' }}
              />
              <p className="text-sm text-gray-400 mt-2">
                This will be displayed on your donation page to help viewers understand your goals
              </p>
            </div>

            {/* Preview */}
            <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">Preview</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Title:</span>
                  <span className="text-white">
                    {description || 'Donation Page'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Min Amount:</span>
                  <span className="text-purple-400 font-semibold">
                    {minAmount} SOL
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Your Wallet:</span>
                  <span className="text-blue-400 font-mono text-sm">
                    {publicKey?.toString().slice(0, 8)}...{publicKey?.toString().slice(-8)}
                  </span>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '16px' }}>
              <button
                type="button"
                onClick={handleBack}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex-1"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="spinner mr-2"></div>
                    Creating...
                  </div>
                ) : (
                  'Create Donation Page'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateDonationPage;